import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { formatCurrency } from 'support/helpers/currency/currency';
import { countDecimalPlaces } from 'support/helpers/numbers/numbers';
import { useDocumentCurrency } from 'components/WebEDI/helpers';

import { RoundedCurrencyTooltip } from '../RoundedCurrency';
import { Summary } from '../types';

type SalesReportSummaryValues = {
  taxes: number;
  allowances: number;
  total: number;
  charges: number;
  net: number;
  gross: number;
};

export const useSalesReportSummary = (): Summary => {
  const { t } = useTranslation();
  const currency = useDocumentCurrency();
  const values = useSalesReportSummaryValues();

  return useMemo(() => {
    return {
      content: [
        [
          {
            label: t('webedi:documentSummary.salesReport.gross.label'),
            value: <span className="tabular-nums">{formatCurrency(values.gross, currency)}</span>,
          },
          {
            label: t('webedi:documentSummary.salesReport.allowances.label'),
            value: <span className="tabular-nums">{formatCurrency(values.allowances, currency)}</span>,
          },
          {
            label: t('webedi:documentSummary.salesReport.charges.label'),
            value: <span className="tabular-nums">{formatCurrency(values.charges, currency)}</span>,
          },
        ],
        [
          {
            label: t('webedi:documentSummary.salesReport.net.label'),
            value: <span className="tabular-nums">{formatCurrency(values.net, currency)}</span>,
          },
          {
            label: t('webedi:documentSummary.salesReport.taxes.label'),
            value: <span className="tabular-nums">{formatCurrency(values.taxes, currency)}</span>,
          },
        ],
      ],
      footer: {
        label: t('webedi:documentSummary.salesReport.footer.label'),
        value:
          countDecimalPlaces(values.total) > 2 ? (
            <RoundedCurrencyTooltip value={values.total} currency={currency} type="salesReport" />
          ) : (
            <span className="tabular-nums">
              {formatCurrency(values.total, currency, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          ),
      },
    };
  }, [t, currency, values]);
};

const useSalesReportSummaryValues = (): SalesReportSummaryValues => {
  const [taxes, allowances, total, charges, net, gross] = useWatch({
    name: [
      'info.0.sales_tax_amount',
      'info.0.sales_allowance_amount',
      'info.0.sales_total_amount',
      'info.0.sales_charge_amount',
      'info.0.sales_net_amount',
      'info.0.sales_gross_amount',
    ],
  });

  return useMemo(
    () => ({
      gross: gross ?? 0,
      allowances: allowances ?? 0,
      charges: charges ?? 0,
      net: net ?? 0,
      taxes: taxes ?? 0,
      total: total ?? 0,
    }),
    [total, charges, allowances, taxes, net, gross],
  );
};
